<template>
    <div v-if="loading">
        {{ loadingMessage }}
    </div>
    <div v-else class="card">
        <div class="card-header">
            <div class="row align-items-center">
                <div class="col-sm-6 col-lg-8 col-xxl-6">
                    <h4 class="header-title">{{ $t('installationRooms.listTitle') }}</h4>
                </div>
                <!-- Wider where both buttons show their label, so the search keeps its room -->
                <div class="col-sm-6 col-lg-4 col-xxl-6 mt-2 mt-sm-0">
                    <div class="d-flex">
                        <input type="text" id="searchRoom" ref="searchInput" class="form-control form-control-sm"
                            :placeholder="$t('common.search')" aria-controls="jstree-rooms" maxlength="64"
                            @keyup="onSearchKeyup">
                        <button type="button" class="btn ms-2 btn-primary text-nowrap" :disabled="noLicense"
                            @click="addRoom">
                            <i class="mdi mdi-door-open"></i><span class="d-none d-xxl-inline-block ms-1">{{
                                $t('installationRooms.addRoom') }}</span>
                        </button>
                        <button type="button" id="rooms-licenses" class="btn ms-2 btn-outline-primary text-nowrap"
                            :title="$t('installationRooms.manageLicenses')" @click="manageLicenses">
                            <i class="mdi mdi-key-outline"></i><span class="d-none d-xxl-inline-block ms-1">{{
                                $t('installationRooms.manageLicenses') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-body pt-0">
            <div class="component-treeview-legend tree-rooms-legend">
                <div class="tree-cell component-name">{{ $t('installationRooms.legend.room') }}</div>
                <div class="tree-cell component-type">{{ $t('installationRooms.legend.hub') }}</div>
                <div class="tree-cell component-action">{{ $t('installationRooms.legend.actions') }}</div>
            </div>
            <!-- Same look as the empty row of the other lists' tables, as in Mes interventions -->
            <table v-if="treeRooms.length === 0" class="table table-centered table-striped">
                <tbody>
                    <tr>
                        <td class="text-center">{{ $t(noLicense ? 'installationRooms.noLicense' :
                            'installationRooms.emptyTable') }}</td>
                    </tr>
                </tbody>
            </table>
            <!-- jstree takes over this element's DOM: the key forces a fresh render on each rebuild -->
            <div v-else :key="treeKey" id="jstree-rooms" ref="tree" class="component-treeview tree-rooms">
                <ul>
                    <li v-for="room in treeRooms" :key="room.id"
                        data-jstree='{ "icon" : "mdi mdi-door text-primary", "opened" : true }'>
                        <div class="tree-row">
                            <div class="tree-cell component-name fw-bold text-primary">
                                <TreeButton :path="{ action: 'edit', id: room.id }" :label="room.label" type="link" />
                            </div>
                            <div class="tree-cell component-type"></div>
                            <div class="tree-cell component-action">
                                <TreeButton :path="{ action: 'edit', id: room.id }" color="d-none d-md-inline ms-1" />
                                <TreeButton :path="{ action: 'delete', id: room.id }" type="btn btn-sm btn-danger ms-2"
                                    icon="mdi-delete" :label="$t('common.delete')" color="d-none d-md-inline ms-1" />
                            </div>
                        </div>
                        <ul>
                            <li v-for="reader in room.readers" :key="reader.id"
                                data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                                <div class="tree-row">
                                    <div class="tree-cell component-name fw-bold text-dark">{{ reader.reader_label }}
                                    </div>
                                    <div class="tree-cell component-type">{{ reader.hub_label }}</div>
                                    <div class="tree-cell component-action"></div>
                                </div>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </div>
    </div>
    <RoomModal ref="roomModal" />
    <FacilityLicenseModal ref="licenseModal" />
    <ConfirmationModal ref="confirmDeleteModal" :title="$t('common.confirmDelete')" :question="confirmDeleteQuestion"
        type="danger" :button1="$t('common.cancel')" :button2="$t('common.delete')" :action="deleteRoom" />
</template>

<script>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useRoomStore } from '@/stores/roomStore';
import { showToast } from '@/services/toast';
import Room from '@/models/room';
import TreeButton from '@/components/treeButton';
import ConfirmationModal from '@/modals/confirmationModal.vue';
import RoomModal from '@/modals/roomModal.vue';
import FacilityLicenseModal from '@/modals/facilityLicenseModal.vue';
import 'jstree/dist/themes/default/style.min.css';
import 'jstree';
import $ from 'jquery';

// Labels come from users and the confirmation question renders HTML
function escapeHtml(text) {
    return String(text ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export default {
    name: 'InstallationEditRooms',
    components: {
        TreeButton,
        ConfirmationModal,
        RoomModal,
        FacilityLicenseModal,
    },
    setup() {
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.id);
        const roomStore = useRoomStore();
        const loading = ref(true);
        const loadingMessage = ref(t('common.loading'));
        const tree = ref(null);
        const treeKey = ref(0);
        // Snapshot shown by the tree: jstree owns the DOM once built, so the
        // store changes are only reflected through rebuildTree()
        const treeRooms = ref([]);
        const searchInput = ref(null);
        const roomModal = ref(null);
        const licenseModal = ref(null);
        const confirmDeleteModal = ref(null);
        const confirmDeleteQuestion = ref('');
        // No HAXO BOOKING license on the installation: no room can be added,
        // the button is greyed and the empty list says why. Read when the tab
        // opens and again when a license is added or deleted from it; one
        // changed in the app shows once the tab is reloaded.
        const noLicense = ref(false);
        let searchTimeout = null;

        function snapshotRooms() {
            treeRooms.value = [...roomStore.rooms].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        }

        function onTreeButtonClick(event) {
            const params = JSON.parse(event.currentTarget.getAttribute('data-url'));
            const room = roomStore.rooms.find(r => r.id === params.id);
            if (!room) return;
            switch (params.action) {
                case 'edit':
                    editRoom(room);
                    break;
                case 'delete':
                    confirmDeleteQuestion.value = t('installationRooms.confirmDeleteQuestion', { label: escapeHtml(room.label) });
                    confirmDeleteModal.value.show(room.id);
                    break;
            }
        }

        function initTree() {
            if (!tree.value) return;
            $(tree.value).jstree({
                plugins: ['search'],
                core: {
                    themes: {
                        responsive: false
                    }
                },
                search: {
                    show_only_matches: true,
                    show_only_matches_children: true
                }
            });
            $(tree.value).on('click', '.tree-button', onTreeButtonClick);
            // Keep the current filter across rebuilds
            search();
        }

        function destroyTree() {
            if (tree.value && $(tree.value).jstree(true)) {
                $(tree.value).jstree('destroy');
            }
        }

        async function rebuildTree() {
            destroyTree();
            snapshotRooms();
            treeKey.value++;
            await nextTick();
            initTree();
        }

        function search() {
            const treeInstance = tree.value ? $(tree.value).jstree(true) : null;
            if (!treeInstance) return;
            const searchText = (searchInput.value?.value ?? '').trim().toLowerCase();
            treeInstance.search(searchText);
            $(tree.value).find('.highlighted').removeClass('highlighted');
            if (searchText.length > 0) {
                $(tree.value).find('.jstree-search').each(function () {
                    const node = $(this);
                    if (node.text().toLowerCase().includes(searchText)) {
                        node.addClass('highlighted');
                    }
                });
            }
        }

        function onSearchKeyup() {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(search, 250);
        }

        onMounted(async () => {
            try {
                const [, places] = await Promise.all([roomStore.load(facilityId), roomStore.loadRoomPlaces(facilityId)]);
                noLicense.value = places === 0;
                snapshotRooms();
                loading.value = false;
                await nextTick();
                initTree();
            } catch (error) {
                loadingMessage.value = error;
            }
        });

        onBeforeUnmount(() => {
            clearTimeout(searchTimeout);
            destroyTree();
        });

        async function addRoom() {
            // A new room takes a place in the installation's HAXO BOOKING
            // licenses: when none is free, the modal says so at once rather
            // than the save (402). Asked again on each opening, a license may
            // just have been activated in the app.
            const places = await roomStore.loadRoomPlaces(facilityId);
            const licenseNeeded = places != null && roomStore.rooms.length >= places;
            const info = await roomModal.value.show(new Room(), roomStore.readersByHub, licenseNeeded);
            if (!info) return;
            try {
                await roomStore.createRoom(facilityId, info);
                await rebuildTree();
                showToast('success', t('installationRooms.created'));
            } catch (error) {
                showToast('error', error);
            }
        }

        // Licenses of the installation itself, as in the app: a HAXO BOOKING
        // one added or deleted changes at once whether rooms can be added
        async function manageLicenses() {
            const changed = await licenseModal.value.show(facilityId);
            if (!changed) return;
            const places = await roomStore.loadRoomPlaces(facilityId);
            noLicense.value = places === 0;
        }

        async function editRoom(room) {
            const info = await roomModal.value.show(room, roomStore.readersByHub);
            if (!info) return;
            try {
                await roomStore.updateRoom(facilityId, room.id, info);
                await rebuildTree();
                showToast('success', t('installationRooms.updated'));
            } catch (error) {
                showToast('error', error);
            }
        }

        async function deleteRoom(id) {
            try {
                await roomStore.deleteRoom(facilityId, id);
                await rebuildTree();
                showToast('success', t('installationRooms.deleted'));
            } catch (error) {
                showToast('error', error);
            }
        }

        return {
            loading,
            loadingMessage,
            tree,
            treeKey,
            treeRooms,
            searchInput,
            roomModal,
            licenseModal,
            confirmDeleteModal,
            confirmDeleteQuestion,
            noLicense,
            onSearchKeyup,
            manageLicenses,
            addRoom,
            deleteRoom,
        };
    },
};
</script>

<style scoped>
.tree-rooms-legend .tree-cell.component-action {
    text-align: center;
    min-width: 180px;
    width: 180px;
}

.tree-rooms .tree-row .tree-cell.component-type {
    font-style: italic;
    opacity: 0.75;
}

.tree-rooms .tree-row .tree-cell.component-action {
    text-align: right;
    min-width: 180px;
    width: 180px;
}

@media (max-width: 767px) {
    .tree-rooms-legend {
        display: none;
    }

    .tree-rooms .tree-row .tree-cell.component-name {
        width: 100%;
        padding-bottom: 0;
        padding-right: 90px;
    }

    .tree-rooms .tree-row .tree-cell.component-type {
        width: 100%;
        padding-top: 0;
        text-align: left;
    }

    .tree-rooms .tree-row .tree-cell.component-action {
        position: absolute;
        top: 0.5rem;
        right: 0;
        padding: 0;
        width: 90px;
        min-width: 90px;
    }
}
</style>
